import * as React from 'react'
import { useDesignV2 } from '@/hooks/use-design-v2'
import { ICONS, type IconName } from './icons'

export interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'ref'> {
  name: IconName
  /** Accessible name. Omit for decorative icons (rendered aria-hidden). */
  label?: string
  size?: number | string
  /** Force a family regardless of the flag (showcase / tests). */
  family?: 'lucide' | 'phosphor'
  /** Phosphor weight (v2 only). The system default is regular; use bold/fill sparingly. */
  weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'
}

/**
 * <Icon name="close" /> — renders the lucide glyph under design v1 (pixel-identical to today)
 * and the Phosphor regular glyph under design v2. Size and colour follow className/currentColor,
 * exactly like a direct lucide import, so `className="size-4"` keeps working.
 */
export const Icon = React.forwardRef<SVGSVGElement, IconProps>(function Icon(
  { name, label, size, family, weight = 'regular', className, ...rest },
  ref,
) {
  const v2 = useDesignV2()
  const useP = family ? family === 'phosphor' : v2
  const entry = ICONS[name]
  const a11y = label
    ? { role: 'img', 'aria-label': label }
    : { 'aria-hidden': true as const, focusable: 'false' as const }

  if (useP) {
    const P = entry.phosphor
    return (
      <P
        weight={weight}
        size={size ?? '1.15em'}
        className={className}
        data-icon={name}
        ref={ref}
        {...a11y}
        {...(rest as Record<string, unknown>)}
      />
    )
  }
  const L = entry.lucide
  return (
    <L
      size={size ?? 24}
      className={className}
      data-icon={name}
      ref={ref}
      {...a11y}
      {...(rest as Record<string, unknown>)}
    />
  )
})

export type { IconName }
